import { useState, useEffect, useRef } from 'react';
import type { ApiResponse } from '@/api/shared/types';


interface SelectOption {
  value: string;
  label: string;
}

export interface CatalogLoaderResult<T> {
  data: T[];
  options: SelectOption[];
  isLoaded: boolean;
}

/**
 * Generic hook to load catalog data with consistent loading state management.
 * Handles the common pattern of fetching data, tracking loaded state,
 * and mapping to select options.
 *
 * The hook creates an AbortController and passes its signal to `fetchFunction`
 * so callers can forward it to the API layer (e.g. for server-side timeout
 * merging via `AbortSignal.any`). However, the cleanup does NOT abort the
 * controller — catalog fetches are small one-shot requests that can safely
 * complete in the background. Aborting them would cause visible "Canceled"
 * entries in the network tab during React StrictMode double-mounts.
 * Stale state updates are prevented with a boolean flag instead.
 *
 * @param fetchFunction - Async function that receives an AbortSignal and returns catalog data.
 * @param mapper - Function to transform each item into a SelectOption (value/label pair)
 * @param dependencies - Array of dependencies that trigger a refetch when changed
 * @param skipFetch - Optional condition to skip fetching (useful when required params are missing)
 * @param onError - Optional callback invoked when the fetch fails or throws an exception
 *
 * @example
 * const locations = useCatalogLoader(
 *   (signal) => locationService.getEmployeeEnabledLocations(employeeId, signal),
 *   (loc) => ({ value: String(loc.ubicationId), label: loc.ubicationName || '' }),
 *   [employeeId],
 *   !employeeId,
 *   () => showNotification('error', translate('common:errors.loadingData'), 5000),
 * );
 */
export function useCatalogLoader<T>(
  fetchFunction: (signal: AbortSignal) => Promise<ApiResponse<T[]>>,
  mapper: (item: T) => SelectOption,
  dependencies: unknown[] = [],
  skipFetch: boolean = false,
  onError?: (response: ApiResponse<T[]> | null) => void,
): CatalogLoaderResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (skipFetch) {
      setData([]);
      setOptions([]);
      setIsLoaded(true);
      return;
    }

    let stale = false;
    const controller = new AbortController();

    void (async () => {
      try {
        const response = await fetchFunction(controller.signal);

        if (stale) return;

        if (response.success && response.data) {
          const items = response.data;
          setData(items);
          setOptions(items.map(mapper));
        } else {
          setData([]);
          setOptions([]);
          onErrorRef.current?.(response);
        }
      } catch {
        if (stale) return;
        setData([]);
        setOptions([]);
        onErrorRef.current?.(null);
      } finally {
        if (!stale) {
          setIsLoaded(true);
        }
      }
    })();

    return () => {
      stale = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, skipFetch]);

  return { data, options, isLoaded };
}
